import { createApiService } from './api';
import { unwrapPaginatedResponse } from '@/utils/pagination';
import type {
  NotificationTemplate,
  NotificationTemplateRequest,
  ApiResponse,
  PaginatedResponse,
  ListTemplatesParams,
  TestTemplateRequest,
} from '@/types/notification';

// Notification Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const NOTIFICATION_API_BASE_URL =
  import.meta.env.VITE_NOTIFICATION_API_BASE_URL || '/api/notification';

const notificationApiService = createApiService(NOTIFICATION_API_BASE_URL);

// Helper to unwrap ApiResponse (handles both wrapped and direct responses)
const unwrapResponse = <T>(response: ApiResponse<T> | T): T => {
  // If response is already the data type (no wrapper)
  if (response && typeof response === 'object' && !('success' in response)) {
    return response as T;
  }
  // If response is wrapped in ApiResponse
  const wrapped = response as ApiResponse<T>;
  if (!wrapped.success || !wrapped.data) {
    throw new Error(wrapped.message || 'API request failed');
  }
  return wrapped.data;
};

// ============================================================================
// NotificationTemplate APIs
// ============================================================================

export const notificationTemplateApi = {
  // List templates with pagination and filters
  list: async (params?: ListTemplatesParams): Promise<PaginatedResponse<NotificationTemplate>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.direction) queryParams.append('direction', params.direction);
    if (params?.channel) queryParams.append('channel', params.channel);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.locale) queryParams.append('locale', params.locale);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    try {
      const response = await notificationApiService.get<unknown>(
        `/api/v1/notifications/templates${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<NotificationTemplate>(response);
    } catch (error: unknown) {
      console.error('[notificationTemplateApi.list] Error fetching templates:', error);
      throw error;
    }
  },

  // Get template by ID
  getById: async (templateId: string, locale?: string): Promise<NotificationTemplate> => {
    const queryParams = new URLSearchParams();
    if (locale) queryParams.append('locale', locale);

    const query = queryParams.toString();
    const response = await notificationApiService.get<ApiResponse<NotificationTemplate>>(
      `/api/v1/notifications/templates/${templateId}${query ? `?${query}` : ''}`
    );
    return unwrapResponse(response);
  },

  // Create template
  create: async (data: NotificationTemplateRequest): Promise<NotificationTemplate> => {
    const response = await notificationApiService.post<ApiResponse<NotificationTemplate>>(
      '/api/v1/notifications/templates',
      data
    );
    return unwrapResponse(response);
  },

  // Update template (full)
  update: async (templateId: string, data: NotificationTemplateRequest): Promise<NotificationTemplate> => {
    const response = await notificationApiService.put<ApiResponse<NotificationTemplate>>(
      `/api/v1/notifications/templates/${templateId}`,
      data
    );
    return unwrapResponse(response);
  },

  // Update status
  updateStatus: async (templateId: string, status: string): Promise<NotificationTemplate> => {
    const response = await notificationApiService.patch<ApiResponse<NotificationTemplate>>(
      `/api/v1/notifications/templates/${templateId}/status?status=${status}`,
      {}
    );
    return unwrapResponse(response);
  },

  // Delete template
  delete: async (templateId: string): Promise<void> => {
    const response = await notificationApiService.delete<ApiResponse<void>>(
      `/api/v1/notifications/templates/${templateId}`
    );
    unwrapResponse(response);
  },

  // Validate template
  // Note: API expects a string (template content), not an object
  validate: async (templateId: string, content: string): Promise<boolean> => {
    const response = await notificationApiService.post<ApiResponse<boolean>>(
      `/api/v1/notifications/templates/${templateId}/validate`,
      content
    );
    const result = unwrapResponse(response);
    return result === true;
  },

  // Test template (render with variables)
  // API expects an object with variable key-value pairs (additionalProperties: true)
  // Accepts either TestTemplateRequest (with variables property) or Record<string, unknown> directly
  test: async (
    templateId: string,
    variables?: TestTemplateRequest | Record<string, unknown>,
    locale?: string
  ): Promise<string> => {
    const queryParams = new URLSearchParams();
    if (locale) queryParams.append('locale', locale);

    const query = queryParams.toString();
    // API expects the variables object directly (not wrapped in a 'variables' key)
    // Extract variables from TestTemplateRequest if it has a variables property
    let payload: Record<string, unknown> = {};
    if (variables) {
      if ('variables' in variables && variables.variables) {
        // If it's TestTemplateRequest with variables property, extract it
        payload = variables.variables as Record<string, unknown>;
      } else {
        // If it's already a Record<string, unknown>, use it directly
        payload = variables as Record<string, unknown>;
      }
    }
    const response = await notificationApiService.post<ApiResponse<string>>(
      `/api/v1/notifications/templates/${templateId}/test${query ? `?${query}` : ''}`,
      payload
    );
    return unwrapResponse(response);
  },
};

export { notificationApiService };








